import React from "react";
import { useTranslation } from "react-i18next";

import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { InformationIcon } from "~/components/ui/icons/information";
import { InvoiceStatus, InvoiceType } from "~/graphql/graphql-new.gen";
import { getInvoiceLockReason } from "~/utils/billing";

import { DownPaymentSummary } from "./down-payment-summary.jsx";
import { DownPaymentUpload } from "./down-payment-upload.jsx";
import { InvoiceLockedSaleEndedNeedActions } from "./invoice-locked-sale-ended-need-actions";
import { InvoiceLockedSaleEndedWaitingWithdrawal } from "./invoice-locked-sale-ended-waiting-withdrawal";
import { InvoiceLockedSaleOngoing } from "./invoice-locked-sale-ongoing";
import { InvoiceLockedSaleUpcoming } from "./invoice-locked-sale-upcoming";
import { InvoiceLockedUnlockClassicDownPayment } from "./invoice-locked-unlock-classic-down-payment.jsx";
import { InvoiceLockedUnlockConditions } from "./invoice-locked-unlock-conditions.jsx";
import { InvoiceLockedUnlockExceptionalDownPayment } from "./invoice-locked-unlock-exceptional-down-payment.jsx";
import { useBillingState } from "./use-billing-state";
import { useBilling } from "./use-billing.js";
import { useInvoices } from "./use-invoices.js";

const SaleStatusBanner: React.FC = () => {
  const { t } = useTranslation();
  const { data: billingState } = useBillingState();

  if (!billingState) {
    return null;
  }

  switch (getInvoiceLockReason(billingState)) {
    case "sale-upcoming":
      return <InvoiceLockedSaleUpcoming />;
    case "sale-ongoing":
      return <InvoiceLockedSaleOngoing />;
    case "pending-actions":
      return <InvoiceLockedSaleEndedNeedActions />;
    case "waiting-withdrawal":
      return <InvoiceLockedSaleEndedWaitingWithdrawal />;
    default:
      return (
        <Alert>
          <InformationIcon className="size-10" />
          <AlertTitle>
            {t("invoice.invoice_locked.default.invoice_locked")}
          </AlertTitle>
          <AlertDescription>
            {t("invoice.invoice_locked.default.contact_your_am")}
          </AlertDescription>
        </Alert>
      );
  }
};

type InvoiceLockedProps = {
  isSubjectVat: boolean;
};

export const InvoiceLocked: React.FC<InvoiceLockedProps> = ({
  isSubjectVat,
}) => {
  const { t } = useTranslation();
  const { data: billing } = useBilling();
  const { data: billingState } = useBillingState();
  const { data: invoices } = useInvoices();

  if (!billingState || !invoices) {
    return null;
  }

  const downPayments = invoices.filter(
    (invoice) => invoice.type === InvoiceType.DownPayment,
  );
  const showDownPayment =
    billing?.down_payment &&
    (downPayments.length === 0 ||
      downPayments.every(
        (invoice) =>
          invoice.status !== InvoiceStatus.Validated &&
          invoice.status !== InvoiceStatus.ToBePaid &&
          invoice.status !== InvoiceStatus.Paid,
      ));

  return (
    <>
      <div className="flex flex-col gap-10">
        {!showDownPayment &&
        ((billing?.payments.length ?? 0) > 0 || invoices.length > 0) ? (
          <h3 className="text-2xl font-bold text-gray-900">
            {t("invoice.rest_of_the_bill")}
          </h3>
        ) : null}
        {showDownPayment ? (
          <h3 className="text-2xl font-bold text-gray-900">
            {t("invoice.down_payment")}
          </h3>
        ) : null}
        <SaleStatusBanner />
        {!showDownPayment ? <InvoiceLockedUnlockConditions /> : null}
        <InvoiceLockedUnlockClassicDownPayment />
        {showDownPayment ? <InvoiceLockedUnlockExceptionalDownPayment /> : null}
        <DownPaymentUpload isSubjectVat={isSubjectVat} />
      </div>
      <DownPaymentSummary isSubjectVat={isSubjectVat} />
    </>
  );
};
